// One tone per kind of waiting thing, so every place that counts it (the
// sidebar, Home's attention cards, the screens' tab counts, the companion)
// ranks it the same way. Colour is kept for what is late or broken; a
// count that only says how many things wait is grey. Loudest first:
//
//   danger     something is broken: a deployment failed or did not start
//              (one never verified is only a warning)
//   warning    against the clock: overdue todos
//   attention  a review queue: skills to approve (a grey count, like default)
//   default    waiting in the ordinary way: meetings that need your notes,
//              tasks waiting for your OK, todos due today with none overdue
//
// Rendering a tone is up to the surface, but a COUNT PILL renders it as
// COUNT_PILL_TONE below: one filled pill, red for broken, amber for late,
// grey otherwise.

export type AttentionKind = "meetings" | "skills" | "deployments" | "todos" | "tasks";
export type AttentionTone = "default" | "attention" | "warning" | "danger";

/** `overdue`: todos past their due date (only the todos tone reads it).
 *  `failing`: deployments that failed their check or did not start. When it
 *  is known and zero, the apps that are "down" were only never verified
 *  (they may well be running), which is a warning, not danger. */
export function attentionTone(
  kind: AttentionKind,
  opts: { overdue?: number; failing?: number } = {},
): AttentionTone {
  switch (kind) {
    case "deployments":
      return opts.failing === 0 ? "warning" : "danger";
    case "meetings":
    case "tasks":
      // waiting for your OK is a count of things waiting, not something late
      return "default";
    case "todos":
      return (opts.overdue ?? 0) > 0 ? "warning" : "default";
    case "skills":
      return "attention";
  }
}

/** A 20px count pill in each tone (sidebar counts; tab counts should match). */
export const COUNT_PILL_TONE: Record<AttentionTone, string> = {
  danger: "bg-danger-fill text-on-danger",
  warning: "bg-warning/15 text-warning",
  attention: "bg-raised text-fg-muted",
  default: "bg-raised text-fg-muted",
};

/** The tone a shared TabCount pill takes to look like COUNT_PILL_TONE (its "attention" is an accent tint). */
export function countPillTone(tone: AttentionTone): Exclude<AttentionTone, "attention"> {
  return tone === "attention" ? "default" : tone;
}

/** The same tones as an 8px dot (the icon rail's counts). */
export const COUNT_DOT_TONE: Record<AttentionTone, string> = {
  danger: "bg-danger-fill",
  warning: "bg-warning",
  attention: "bg-fg-subtle",
  default: "bg-fg-subtle",
};
